/* SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial */
import { useEffect, useState, type ReactNode } from "react";
import { changeConferenceMedia } from "../state/ConferenceMedia";
import { RoomEvent } from "livekit-client";
import { constant } from "../state/Behavior";
import { useMediaDevices } from "../MediaDevicesContext";
import { requestConferenceInput, conferenceDeviceError$, type AudioOutputDeviceLabel } from "../state/MediaDevices";
import { type MuteStates } from "../state/MuteStates";
import { type CallViewModel } from "../state/CallViewModel/CallViewModel";
import { useBehavior } from "../useBehavior";
import { widget } from "../widget";
import styles from "./ConferenceControls.module.css";

const emptyRooms$ = constant<CallViewModel["livekitRoomItems$"]["value"]>([]);
const paths: Record<string, ReactNode> = {
  mic: <><rect x="9" y="2" width="6" height="13" rx="3"/><path d="M5 10v2a7 7 0 0014 0v-2M12 19v3m-4 0h8"/></>,
  camera: <><rect x="2" y="5" width="14" height="14" rx="3"/><path d="m16 9 6-3v12l-6-3"/></>,
  chat: <path d="M21 11a9 9 0 01-9 9H4l-3 3v-11a10 10 0 0120-1Z"/>,
  share: <><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8m-4-4v4m-4-11 4-4 4 4m-4-4v8"/></>,
  headphones: <><path d="M4 13v-3a8 8 0 0116 0v3"/><rect x="2" y="12" width="5" height="9" rx="2"/><rect x="17" y="12" width="5" height="9" rx="2"/></>,
  speaker: <><path d="m3 9 4 0 5-5v16l-5-5H3ZM16 8a6 6 0 010 8m3-11a10 10 0 010 14"/></>,
  phone: <><rect x="6" y="2" width="12" height="20" rx="3"/><path d="M10 18h4"/></>,
  leave: <path d="M2 15v-4a17 17 0 0120 0v4l-6-1v-4a12 12 0 00-8 0v4Z"/>,
  close: <path d="m6 6 12 12M6 18 18 6"/>,
};
function Icon({ name, off = false }: { name: string; off?: boolean }): ReactNode {
  return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths[name]}{off && <path d="m2 2 20 20"/>}</svg>;
}
function label(value: AudioOutputDeviceLabel): string {
  return value.type === "name" ? value.name : value.type === "number" ? `Device ${value.number}` : value.type === "default" ? value.name || "System default" : value.type === "speaker" ? "Speaker" : "Phone";
}
export function ConferenceControls({ muteStates, vm }: { muteStates: MuteStates; vm: CallViewModel }): ReactNode {
  const devices = useMediaDevices();
  const rooms = useBehavior(vm.livekitRoomItems$ ?? emptyRooms$);
  const [playbackBlocked, setPlaybackBlocked] = useState(false);
  useEffect(() => {
    const update = (): void => setPlaybackBlocked(rooms.some(({ livekitRoom }) => !livekitRoom.canPlaybackAudio));
    rooms.forEach(({ livekitRoom }) => livekitRoom.on(RoomEvent.AudioPlaybackStatusChanged, update));
    update();
    return () => { rooms.forEach(({ livekitRoom }) => livekitRoom.off(RoomEvent.AudioPlaybackStatusChanged, update)); };
  }, [rooms]);
  const resumeAudio = (): void => { rooms.forEach(({ livekitRoom }) => { void livekitRoom.startAudio().catch(() => setPlaybackBlocked(true)); }); };
  const nativeOutput = new URLSearchParams(location.search).get("companiesosNative") === "true";
  const audio = useBehavior(muteStates.audio.enabled$);
  const video = useBehavior(muteStates.video.enabled$);
  const inputs = useBehavior(devices.audioInput.available$);
  const outputs = useBehavior(devices.audioOutput.available$);
  const input = useBehavior(devices.audioInput.selected$);
  const output = useBehavior(devices.audioOutput.selected$);
  const [nativeRoute, setNativeRoute] = useState<{ name: string; headphones: boolean } | null>(null);
  useEffect(() => {
    const api = widget?.api;
    if (!api) return;
    const update = (event: CustomEvent): void => {
      event.preventDefault();
      setNativeRoute({ name: String(event.detail.data?.name || "Phone"), headphones: event.detail.data?.headphones === true });
      api.transport.reply(event.detail, {});
    };
    api.on("action:io.companiesos.audio_route_state", update);
    return () => { api.off("action:io.companiesos.audio_route_state", update); };
  }, []);
  const [devicesOpen, setDevicesOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    const subscription = conferenceDeviceError$.subscribe(() => setError("Media access needs attention. Check permission or reconnect your selected device, then retry."));
    return () => subscription.unsubscribe();
  }, []);
  const outputName = output && outputs.get(output.id);
  const outputLabel = nativeRoute?.name || (outputName ? label(outputName) : "System audio output");
  const outputIcon = nativeRoute?.headphones || /head|airpod|buds|bluetooth/i.test(outputLabel) ? "headphones" : /iPhone|iPad|Android/i.test(navigator.userAgent) ? "phone" : "speaker";
  const toggle = async (kind: "audio" | "video"): Promise<void> => {
    if (busy) return;
    resumeAudio();
    setBusy(kind); setError("");
    const model = muteStates[kind];
    const desired = !model.enabled$.value;
    try {
      const enabled = await changeConferenceMedia(model, kind === "audio" ? "audioinput" : "videoinput", desired);
      if (enabled !== desired) throw new Error("CaptureUnavailable");
    } catch {
      setError(`${kind === "audio" ? "Microphone" : "Camera"} could not start. Check this site's permission, then tap the control to retry.`);
    } finally { setBusy(null); }
  };
  const send = (action: string): void => { void widget?.api.transport.send(`io.companiesos.${action}`, {}).catch(() => {}); };
  return <div className={styles.root}>
    {playbackBlocked && <button className={styles.playback} aria-label="Enable conference audio" title="Enable conference audio" onClick={resumeAudio}><Icon name="speaker"/></button>}
    {error && <div role="status" className={styles.notice}>{error}<button aria-label="Dismiss message" onClick={() => setError("")}><Icon name="close"/></button></div>}
    {devicesOpen && <section className={styles.devices} aria-label="Audio devices">
      <header><strong>Audio devices</strong><button aria-label="Close audio devices" onClick={() => setDevicesOpen(false)}><Icon name="close"/></button></header>
      <label>Microphone<select value={input?.id || ""} onChange={event => devices.audioInput.select(event.target.value)}>{[...inputs].map(([id, name]) => <option key={id} value={id}>{label(name)}</option>)}</select></label>
      <label>Sound output<select disabled={nativeOutput || !outputs.size} value={output?.id || ""} onChange={event => devices.audioOutput.select(event.target.value)}>{outputs.size ? [...outputs].map(([id, name]) => <option key={id} value={id}>{label(name)}</option>) : <option>{outputLabel}</option>}</select></label>
      {nativeOutput && <button className={styles.route} onClick={() => send("audio_route")}>Choose output on device</button>}
      {!nativeOutput && !outputs.size && <p className={styles.source}>Choose headphones or speakers in your device's sound controls.</p>}
      <a href="/conference-source.tar.gz" className={styles.source}>Conference source</a>
    </section>}
    <nav className={styles.bar} aria-label="Conference controls">
      <button aria-label={audio ? "Mute microphone" : "Unmute microphone"} aria-pressed={!audio} disabled={busy === "audio"} onClick={() => void toggle("audio")}><Icon name="mic" off={!audio}/></button>
      <button aria-label={`Audio devices: ${outputLabel}`} title={outputLabel} aria-expanded={devicesOpen} onClick={() => { resumeAudio(); if (!devicesOpen && [...inputs.values()].some(value => value.type === "number")) void requestConferenceInput("audioinput").catch(() => setError("Allow microphone access to list audio devices.")); setDevicesOpen(!devicesOpen); }}><Icon name={outputIcon}/></button>
      <button aria-label={video ? "Turn camera off" : "Turn camera on"} aria-pressed={video} disabled={busy === "video"} onClick={() => void toggle("video")}><Icon name="camera" off={!video}/></button>
      {vm.toggleScreenSharing && <button aria-label="Share screen" onClick={vm.toggleScreenSharing}><Icon name="share"/></button>}
      <button aria-label="Conference chat" onClick={() => send("chat")}><Icon name="chat"/></button>
      <button className={styles.leave} aria-label="Leave conference" onClick={vm.hangup}><Icon name="leave"/></button>
    </nav>
  </div>;
}
